import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {ImageDetails} from "@vesta/janus-compatibility-module/utilities/typeDefinitions";
import {imageDetailsValidator as imageDetailsZodValidator} from "@vesta/janus-compatibility-module/utilities/zodValidationPatterns";

export enum EratoDataTypes {
    // why two of them, cant we merge them ??
    text = "text",
    string = "string",
    numeric = "numeric",
    date = "date",
    timestamp = "timestamp",
    boolean = "boolean",
}

export enum EratoColumnTypes {
    metric = "metric",
    dimension = "dimension",
}

export enum EratoMetricAggregationTypes {
    sum = "sum",
    derived = "derived",
    weightedSum = "weighted_sum",
    average = "average",
    count = "count",
    distinctCount = "distinct_count",
    derivedCountDn = "derived_count_dn",
    derivedCountNd = "derived_count_nd",
    derivedCountNn = "derived_count_nn",
    derivedCountDd = "derived_count_dd",
    derivedCountSn = "derived_count_sn",
    derivedCountNs = "derived_count_ns",
    derivedCountSd = "derived_count_sd",
    derivedCountDs = "derived_count_ds",
}

export type EratoRawData = {
    columnMetadata: Array<{
        id: Uuid;
        columnIndex: number;
        displayName: string;
        dataType: EratoDataTypes;
        columnType: EratoColumnTypes;
    }>;
    dataPoints: Array<
        Array<{
            value: string | number | null;
        }>
    >;
};

export type Metric = {
    id: Uuid;
    displayName: string;
    columnName: string;
    type: EratoDataTypes;
    description: string;
    aggregationType: EratoMetricAggregationTypes | null;
    multiplier: number | null;
    numerator: string | null;
    denominator: string | null;
    metric: string | null;
    metricWeight: string | null;
    canonicalId: Uuid;
    agentContext: string;
};

const metricZodValidator: zod.ZodType<Metric> = zod.object({
    id: uuidZodValidator,
    displayName: zod.string(),
    columnName: zod.string(),
    type: zod.enum(EratoDataTypes),
    description: zod.string(),
    aggregationType: zod.enum(EratoMetricAggregationTypes).optional().nullable().transform(value => value === undefined ? null : value),
    multiplier: zod.number().optional().nullable().transform(value => value === undefined ? null : value),
    numerator: zod.string().optional().nullable().transform(value => value === undefined ? null : value),
    denominator: zod.string().optional().nullable().transform(value => value === undefined ? null : value),
    metric: zod.string().optional().nullable().transform(value => value === undefined ? null : value),
    metricWeight: zod.string().optional().nullable().transform(value => value === undefined ? null : value),
    canonicalId: uuidZodValidator,
    agentContext: zod.string().optional().transform(value => value ?? ""),
});

export type Dimension = {
    id: Uuid;
    displayName: string;
    columnName: string;
    type: EratoDataTypes;
    description: string;
    canonicalId: Uuid;
    agentContext: string;
};

const dimensionZodValidator: zod.ZodType<Dimension> = zod
    .object({
        id: uuidZodValidator,
        displayName: zod.string(),
        columnName: zod.string(),
        type: zod.enum(EratoDataTypes),
        description: zod.string(),
        canonicalId: uuidZodValidator,
        agentContext: zod.string().optional().transform(value => value ?? ""),
    });

export const datasetMetadataZodValidator: zod.ZodType<DatasetMetadata> = zod.object({
    agentContext: zod.string().optional().transform(value => value ?? ""),
    datasetId: uuidZodValidator,
    datasetTemplateId: uuidZodValidator.nullable(),
    displayName: zod.string(),
    description: zod.string(),
    healthStatus: uuidZodValidator,
    imageDetails: imageDetailsZodValidator.nullable(),
}).transform(
    (obj) => (
        {
            id: obj.datasetId,
            datasetTemplateId: obj.datasetTemplateId,
            displayName: obj.displayName,
            description: obj.description,
            agentContext: obj.agentContext,
            healthStatus: obj.healthStatus,
            imageDetails: obj.imageDetails,
        }
    ),
);

export type DatasetMetadata = {
    id: Uuid;
    datasetTemplateId: Uuid | null;
    displayName: string;
    description: string;
    agentContext: string;
    healthStatus: Uuid;
    imageDetails: ImageDetails | null;
};

const datasetHealthStatusDisplayNameById: Record<string, string> = {
    "533636e9-abd0-49ae-97b5-16756a48b62e": "Healthy",
    "3086f256-e0df-48d9-8155-44d05433c112": "Unhealthy",
    "edcfde68-9034-496c-8bb6-ae21ddddfa26": "Deleted",
    "a550292c-db25-45d7-8c1d-58c085b24f0c": "Stale",
    "95e5673b-eece-4f2b-9dc6-5ca7c6fd648b": "Hollow",
};

export function getDatasetHealthStatusDisplayName(healthStatus: string): string {
    return datasetHealthStatusDisplayNameById[healthStatus] ?? "Unknown";
}

export type Dataset = DatasetMetadata & {
    metrics: Array<Metric>;
    dimensions: Array<Dimension>;
};

export enum EratoFilterOperation {
    equals = "bafb6b6c-90ca-42bc-9ee9-2ac60ff6c3e7",
    in = "4e61f5af-3ed9-4ae9-befd-d8fdcb73ebad",
    between = "1b40b607-8da0-48b0-9ab0-7fe53cab4a1d",
    contains = "0e55fe86-1062-4fe2-95be-92b9ae0c71ea",
    greaterThan = "cdadd5e6-68f6-48e2-b56f-fc41ee91d2bb",
    lessThan = "f5aec8bc-42c4-4062-b82e-cd567d2e0091",
    greaterThanOrEqualTo = "15f683d5-f335-4ff6-8d4f-5cbe55e675bb",
    lessThanOrEqualTo = "f990d2dd-a7e5-46b7-887a-1c907c565e05",
    and = "21049b0c-569f-4a37-9dc5-5fac882fff08",
    or = "a35b6797-6a09-4b61-bf7b-0ff1da3a01df",
}

export type EratoFilter = {
    operation: EratoFilterOperation;
    attribute?: Uuid;
    value?: string;
    values?: Array<string>;
    filters?: Array<EratoFilter>;
};

export type EratoOrderBy = {
    field: Uuid;
    direction: "asc" | "desc";
};

// UI-only: one row in the flat filter list
export type FilterRow = {
    id: string;
    attribute: Uuid | null;
    operator: EratoFilterOperation | null;
    value: string;
    valueB: string;   // second value for BETWEEN
    valuesIn: string; // comma-separated string for IN
};

// UI-only: one row in the sort list
export type SortRow = {
    id: string;
    field: Uuid | null;
    direction: "asc" | "desc";
};

type OperatorOption = {uuid: EratoFilterOperation; label: string};

export const OPERATORS_BY_TYPE: Record<EratoDataTypes, Array<OperatorOption>> = {
    [EratoDataTypes.string]: [
        {uuid: EratoFilterOperation.equals, label: "equals"},
        {uuid: EratoFilterOperation.in, label: "in"},
        {uuid: EratoFilterOperation.contains, label: "contains"},
    ],
    [EratoDataTypes.text]: [
        {uuid: EratoFilterOperation.equals, label: "equals"},
        {uuid: EratoFilterOperation.in, label: "in"},
        {uuid: EratoFilterOperation.contains, label: "contains"},
    ],
    [EratoDataTypes.numeric]: [
        {uuid: EratoFilterOperation.equals, label: "="},
        {uuid: EratoFilterOperation.in, label: "in"},
        {uuid: EratoFilterOperation.between, label: "between"},
        {uuid: EratoFilterOperation.greaterThan, label: ">"},
        {uuid: EratoFilterOperation.lessThan, label: "<"},
        {uuid: EratoFilterOperation.greaterThanOrEqualTo, label: "≥"},
        {uuid: EratoFilterOperation.lessThanOrEqualTo, label: "≤"},
    ],
    [EratoDataTypes.date]: [
        {uuid: EratoFilterOperation.equals, label: "="},
        {uuid: EratoFilterOperation.between, label: "between"},
        {uuid: EratoFilterOperation.greaterThan, label: ">"},
        {uuid: EratoFilterOperation.lessThan, label: "<"},
        {uuid: EratoFilterOperation.greaterThanOrEqualTo, label: "≥"},
        {uuid: EratoFilterOperation.lessThanOrEqualTo, label: "≤"},
    ],
    [EratoDataTypes.timestamp]: [
        {uuid: EratoFilterOperation.equals, label: "="},
        {uuid: EratoFilterOperation.between, label: "between"},
        {uuid: EratoFilterOperation.greaterThan, label: ">"},
        {uuid: EratoFilterOperation.lessThan, label: "<"},
        {uuid: EratoFilterOperation.greaterThanOrEqualTo, label: "≥"},
        {uuid: EratoFilterOperation.lessThanOrEqualTo, label: "≤"},
    ],
    [EratoDataTypes.boolean]: [
        {uuid: EratoFilterOperation.equals, label: "equals"},
    ],
};

export const datasetZodValidator: zod.ZodType<Dataset> = zod.object({
    healthStatus: uuidZodValidator,
    datasetId: uuidZodValidator,
    datasetTemplateId: uuidZodValidator.nullable(),
    displayName: zod.string(),
    description: zod.string(),
    agentContext: zod.string().optional(),
    metrics: zod.record(zod.string(), metricZodValidator),
    dimensions: zod.record(zod.string(), dimensionZodValidator),
}).transform(value => ({
    id: value.datasetId,
    datasetTemplateId: value.datasetTemplateId,
    displayName: value.displayName,
    description: value.description,
    agentContext: value.agentContext ?? "",
    healthStatus: value.healthStatus,
    imageDetails: null,
    metrics: Object.values(value.metrics),
    dimensions: Object.values(value.dimensions),
}));
